import axiosInstance from '../axiosInstance';
import type { AxiosResponse } from 'axios';
import type {
  Config,
  DocType,
  Substance,
  SubstanceFilter,
  ActionType,
  CriticalityLevel,
  Privilege,
  SubstanceType,
  InputFormat,
  MaintenanceInterval,
  MeasurementDefinition,
  MeasurementType,
  StandardQuantityOption,
  UnitOfMeasurement,
  UnitOfMeasurementConversion,
  UnitConversionRequestItem,
} from './types';

// Backed by Application/impl/Controllers/ConfigurationController.cs (#4294).

const BASE = '/api/Configuration';

export default {
  // ----- DocType (read-only on Configuration page; CRUD lives at /api/DocType from PR 1235) -----
  async getAllDocTypes(): Promise<AxiosResponse<DocType[]>> {
    try {
      return await axiosInstance.get(`/api/DocType`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- Substance (PR-832, renamed from FluidType) -----
  async getAllSubstances(filter?: SubstanceFilter): Promise<AxiosResponse<Substance[]>> {
    try {
      return await axiosInstance.get(`${BASE}/Substance/GetAll`, { params: filter });
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveSubstance(row: Substance): Promise<AxiosResponse<Substance>> {
    try {
      return await axiosInstance.post(`${BASE}/Substance/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateSubstance(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/Substance/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateSubstance(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/Substance/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteSubstance(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/Substance/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- ActionType -----
  async getAllActionTypes(): Promise<AxiosResponse<ActionType[]>> {
    try {
      return await axiosInstance.get(`${BASE}/ActionType/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveActionType(row: ActionType): Promise<AxiosResponse<ActionType>> {
    try {
      return await axiosInstance.post(`${BASE}/ActionType/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteActionType(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/ActionType/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- CriticalityLevel -----
  async getAllCriticalityLevels(): Promise<AxiosResponse<CriticalityLevel[]>> {
    try {
      return await axiosInstance.get(`${BASE}/CriticalityLevel/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveCriticalityLevel(row: CriticalityLevel): Promise<AxiosResponse<CriticalityLevel>> {
    try {
      return await axiosInstance.post(`${BASE}/CriticalityLevel/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteCriticalityLevel(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/CriticalityLevel/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- MaintenanceInterval (full create + edit; Save with id=0 creates) -----
  async getAllMaintenanceIntervals(): Promise<AxiosResponse<MaintenanceInterval[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MaintenanceInterval/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // Reschedule windows for the Work Order reschedule dialog. Same payload as GetAll but gated on
  // write-workorder (not sysadmin), so reschedulers can read the DaysEarly/DaysLate bounds (#5137).
  async getRescheduleWindows(): Promise<AxiosResponse<MaintenanceInterval[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MaintenanceInterval/RescheduleWindows`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveMaintenanceInterval(row: MaintenanceInterval): Promise<AxiosResponse<MaintenanceInterval>> {
    try {
      return await axiosInstance.post(`${BASE}/MaintenanceInterval/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // Frequency options + labels for the ordinary Task / PM / WO / Calendar forms (login-gated, PR-129).
  async getMaintenanceIntervalOptions(): Promise<AxiosResponse<MaintenanceInterval[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MaintenanceInterval/Options`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteMaintenanceInterval(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/MaintenanceInterval/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- SubstanceType -----
  async getAllSubstanceTypes(): Promise<AxiosResponse<SubstanceType[]>> {
    try {
      return await axiosInstance.get(`${BASE}/SubstanceType/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveSubstanceType(row: SubstanceType): Promise<AxiosResponse<SubstanceType>> {
    try {
      return await axiosInstance.post(`${BASE}/SubstanceType/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteSubstanceType(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/SubstanceType/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async getAllMeasurementTypes(): Promise<AxiosResponse<MeasurementType[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MeasurementType/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveMeasurementType(row: MeasurementType): Promise<AxiosResponse<MeasurementType>> {
    try {
      return await axiosInstance.post(`${BASE}/MeasurementType/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateMeasurementType(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/MeasurementType/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateMeasurementType(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/MeasurementType/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getStandardQuantities(): Promise<AxiosResponse<StandardQuantityOption[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MeasurementType/StandardQuantities`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async getAllUnitsOfMeasurement(): Promise<AxiosResponse<UnitOfMeasurement[]>> {
    try {
      return await axiosInstance.get(`${BASE}/UnitOfMeasurement/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveUnitOfMeasurement(row: Omit<UnitOfMeasurement, 'isActive' | 'isStandard' | 'systemOfMeasurementCode'>): Promise<AxiosResponse<UnitOfMeasurement>> {
    try {
      return await axiosInstance.post(`${BASE}/UnitOfMeasurement/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateUnitOfMeasurement(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/UnitOfMeasurement/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateUnitOfMeasurement(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/UnitOfMeasurement/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async getAllMeasurementDefinitions(): Promise<AxiosResponse<MeasurementDefinition[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MeasurementDefinition/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // Active-only and login-gated, for the assay composer's palette. GetAll is sysadmin AND behind
  // the measurements feature, which is off in production — the composer must not depend on it.
  async listMeasurementDefinitions(): Promise<AxiosResponse<MeasurementDefinition[]>> {
    try {
      return await axiosInstance.get(`${BASE}/MeasurementDefinition/List`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // definitionID -> number of assay template fields referencing it. Drives the "Used by"
  // column and the in-use guard on archive/delete.
  async getMeasurementDefinitionUsage(): Promise<AxiosResponse<Record<string, number>>> {
    try {
      return await axiosInstance.get(`${BASE}/MeasurementDefinition/Usage`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveMeasurementDefinition(
    row: Omit<MeasurementDefinition, 'unitAbbreviation'>,
  ): Promise<AxiosResponse<MeasurementDefinition>> {
    try {
      return await axiosInstance.post(`${BASE}/MeasurementDefinition/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateMeasurementDefinition(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/MeasurementDefinition/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateMeasurementDefinition(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/MeasurementDefinition/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async getAllInputFormats(): Promise<AxiosResponse<InputFormat[]>> {
    try {
      return await axiosInstance.get(`${BASE}/InputFormat/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveInputFormat(row: InputFormat): Promise<AxiosResponse<InputFormat>> {
    try {
      return await axiosInstance.post(`${BASE}/InputFormat/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateInputFormat(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/InputFormat/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateInputFormat(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/InputFormat/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteInputFormat(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.delete(`${BASE}/InputFormat/${id}`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async getAllUnitOfMeasurementConversions(): Promise<AxiosResponse<UnitOfMeasurementConversion[]>> {
    try {
      return await axiosInstance.get(`${BASE}/UnitOfMeasurementConversion/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveUnitOfMeasurementConversion(
    row: Omit<UnitOfMeasurementConversion, 'isActive'>,
  ): Promise<AxiosResponse<UnitOfMeasurementConversion>> {
    try {
      return await axiosInstance.post(`${BASE}/UnitOfMeasurementConversion/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deactivateUnitOfMeasurementConversion(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/UnitOfMeasurementConversion/${id}/Deactivate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async activateUnitOfMeasurementConversion(id: number): Promise<AxiosResponse<void>> {
    try {
      return await axiosInstance.patch(`${BASE}/UnitOfMeasurementConversion/${id}/Activate`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async convertUnitOfMeasurementValue(value: number, fromId: number, toId: number, excludeConversionId?: number): Promise<AxiosResponse<number>> {
    try {
      return await axiosInstance.get(`${BASE}/UnitOfMeasurementConversion/Convert`, {
        params: { value, fromId, toId, excludeConversionId },
      });
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async convertMany(items: UnitConversionRequestItem[]): Promise<AxiosResponse<(number | null)[]>> {
    try {
      return await axiosInstance.post(`${BASE}/UnitOfMeasurementConversion/ConvertMany`, items);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- Privilege (read-only) -----
  async getAllPrivileges(): Promise<AxiosResponse<Privilege[]>> {
    try {
      return await axiosInstance.get(`${BASE}/Privilege/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- System configuration: Config.Config (PR-869, sysadmin) -----
  async getAllConfig(): Promise<AxiosResponse<Config[]>> {
    try {
      return await axiosInstance.get(`${BASE}/Config/GetAll`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveConfig(row: Config): Promise<AxiosResponse<Config>> {
    try {
      return await axiosInstance.post(`${BASE}/Config/Save`, row);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- Environment banner (PR-869) -----
  // Anonymous on the server, so this is safe to call from the login screen before a token exists.
  // Resolves to '' in production, where no Config.Config row is set.
  async getGlobalWarningMessage(): Promise<AxiosResponse<string>> {
    try {
      return await axiosInstance.get(`${BASE}/GlobalWarningMessage`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  // ----- Feature keys (PR-870) -----
  // Login-gated, so it is only callable once a token exists. Returns key -> enabled for the keys
  // this environment declares; an unknown key is absent from the map and reads as disabled.
  async getFeatures(): Promise<AxiosResponse<Record<string, boolean>>> {
    try {
      return await axiosInstance.get(`${BASE}/Features`);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
};
